import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { LinkList } from '../layout/LinkList';
import { cognocracyComponents, type Initiative } from '../../data/initiatives';
import { fadeUp } from '../../utils/motion';
type ProjectFeatureProps = {
  project: Initiative;
};

/** Editorial project feature: emblem on the left, introduction on the right. Reused for every project. */
export function ProjectFeature({
  project
}: ProjectFeatureProps) {
  return <section aria-labelledby={`project-${project.slug}`} className="bg-surface-bg py-20 lg:py-28" data-id="element-1868" data-mp="ilrohrs61dda">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1869" data-mp="wd67hym2pekc">
        <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-id="element-1870" data-mp="wer0hjsqopg3">
          {project.image && <div className="order-2 lg:order-1 lg:col-span-5" data-id="element-1871" data-mp="16aatnyfx5a15">
              <div className="bg-white border border-line rounded-lg p-8 sm:p-10 lg:p-14 flex items-center justify-center" data-id="element-1872" data-mp="168hnsld4qtu7">
                <img src={project.image.src} alt={project.image.alt} className="w-full max-w-[220px] sm:max-w-[280px] lg:max-w-[360px] aspect-square object-contain" data-id="element-1873" data-mp="17eq5lkqurkby" />
              </div>
            </div>}

          <div className={`order-1 lg:order-2 ${project.image ? 'lg:col-span-7' : 'lg:col-span-12 max-w-3xl'}`} data-id="element-1874" data-mp="1le9bs803sllm">
            <p className="text-sm font-medium text-teal mb-3" data-id="element-1875" data-mp="1qdu9yg7g8pxa">{project.category}</p>
            <h2 id={`project-${project.slug}`} className="font-heading font-extrabold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-[1.05]" data-id="element-1876" data-mp="1vcf9edevn5ko">
              {project.name}
            </h2>
            <p className="mt-6 text-xl lg:text-2xl text-ink-dark leading-relaxed" data-id="element-1877" data-mp="2516z0ewgtdsf">{project.description}</p>
            <p className="mt-5 text-lg text-ink-muted leading-relaxed" data-id="element-1878" data-mp="2954wkl656gjd">{project.summary}</p>
            <p className="mt-4 text-sm text-ink-muted" data-id="element-1879" data-mp="2e3yc0j4zohm6">{project.stage}</p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-id="element-1880" data-mp="e05kl1knvxzv">
              <a href={project.href} className="inline-flex items-center gap-2 font-semibold text-lg text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal" data-id="element-1881" data-mp="9zby8cmvar93">
                {project.cta}
                <ArrowRight size={18} aria-hidden="true" data-id="element-1882" data-mp="xqhe2rst5jje" />
              </a>
              <a href="/government" className="inline-flex items-center gap-2 font-medium text-ink-muted hover:text-navy transition-colors duration-150 whitespace-nowrap rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal" data-id="element-1883" data-mp="spa3ym37089k">
                See the NCDS government solution <ArrowRight size={16} aria-hidden="true" data-id="element-1884" data-mp="33io3s1cx8q44" />
              </a>
            </div>
          </div>
        </motion.div>

        <div className="mt-14 lg:mt-16" data-id="element-1885" data-mp="1j0c7id2i7y7x">
          <h3 className="text-sm font-medium text-ink-muted mb-2" data-id="element-1886" data-mp="1nydqyz3udzjx">Built on</h3>
          <LinkList label={`Components ${project.name} is built on`} items={cognocracyComponents.map(component => ({
          title: component.name,
          description: component.body,
          href: component.href
        }))} data-id="element-1887" data-mp="1o0hsjae9qmh1" />
        </div>
      </div>
    </section>;
}